import { useSearch } from '@diamond/shared/context';
import { apiInstance } from '@diamond/shared/utils';
import {
  type UseQueryResult,
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError, CustomParamsSerializer } from 'axios';
import cleanDeep from 'clean-deep';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { captureAdminDataAccessError } from './capture-admin-data-access-error';

interface QueryStringSearch {
  [key: string]: string | number | boolean | string[] | null;
}

type Pagination = { pagination: { rows: number; pages: number } };

// Narrow, typed variant matching TanStack Query's expected keys
type NotifyPropsTyped =
  | Array<keyof UseQueryResult<unknown, unknown>>
  | 'all'
  | (() => Array<keyof UseQueryResult<unknown, unknown>> | 'all' | undefined);

// Backwards-compatible loose input we accept from callers
type NotifyPropsLoose = string[] | 'all' | (() => string[] | 'all' | undefined);

type QueryGetAllOptions<K = unknown, S = K> = {
  staleTime?: number;
  prefetchNextPage?: boolean;
  paramsSerializer?: CustomParamsSerializer;
  enabled?: boolean;
  select?: (data: K) => S;
  notifyOnChangeProps?: NotifyPropsLoose | NotifyPropsTyped;
  notifyOnChangePropsExclusions?:
    | string[]
    | Array<keyof UseQueryResult<unknown, unknown>>;
};
const defaultOptionsQueryGetAll: QueryGetAllOptions = {
  prefetchNextPage: true,
};

/**
 * Creates a wrapper for making various GET requests to an API endpoint with React Query integration.
 * Provides multiple query hooks for different data fetching patterns.
 *
 * @param url - The base URL endpoint for all queries
 * @param token - Optional authentication token to be used with API requests
 *
 * @returns Object containing multiple query hooks:
 *   - useQueryGet: Simple GET request
 *   - useQueryGetAll: Paginated GET request with search functionality
 *   - useQueryGetById: GET request for specific resource by ID
 *   - useQueryData: GET request with manual pagination state management
 *   - useQueryGetByIdPager: GET request by ID with pagination support
 *
 * @example
 * ```typescript
 * const {
 *   useQueryGet,
 *   useQueryGetAll,
 *   useQueryGetById
 * } = QueryWrapper('/api/users', 'auth-token');
 *
 * // In a React component
 * const { data, isLoading } = useQueryGet('users-list');
 * const { data: user } = useQueryGetById('123', 'user-detail');
 * ```
 */
export function QueryWrapper(url: string, token?: string) {
  //page default value
  const page = 1;

  /**
   * Simple GET request hook for fetching data without pagination.
   *
   * @template K - The expected response data type
   * @param queryKey - Unique identifier for the query cache
   * @param params - Optional query parameters to include in the request
   *
   * @returns Object containing:
   *   - data: Response data (defaults to empty array if no data)
   *   - isLoading: Loading state boolean
   *   - refetch: Function to manually refetch the data
   *
   * @example
   * ```typescript
   * const { data, isLoading, refetch } = useQueryGet<User[]>(
   *   'users-active',
   *   { status: 'active', limit: 10 }
   * );
   * ```
   */
  function useQueryGet<K>(
    queryKey: string,
    params?: Readonly<QueryStringSearch>
  ) {
    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(url, {
          params: cleanDeep(params),
        });
        return res.data;
      } catch (error) {
        throw new Error('Terjadi Kesalahan QueryWrapper');
      }
    };

    const { data, isPending, refetch } = useQuery<K>({
      queryKey: [queryKey],
      queryFn: fetchData,
    });

    return {
      data: data ?? [],
      isLoading: isPending,
      refetch,
    };
  }

  /**
   * Advanced paginated GET request hook with search functionality and prefetching.
   * Integrates with search context and supports automatic next page prefetching.
   *
   * @template K - The expected response type that extends Pagination interface
   * @param queryKey - Unique identifier for the query cache
   * @param payload - Optional additional query parameters
   * @param cacheTime - Optional cache time in milliseconds (deprecated, use staleTime in options)
   * @param navigateError - Optional route to navigate to on error
   * @param options - Optional configuration object for query behavior
   *
   * @returns Object containing:
   *   - data: Paginated response data
   *   - pageCount: Total number of pages
   *   - total: Total number of rows
   *   - isLoading: Initial loading state
   *   - isPreviousData: Whether showing previous page data during transition
   *   - isFetching: Whether currently fetching data
   *   - isFetched: Whether data has been fetched at least once
   *   - refetch: Function to manually refetch the data
   *
   * @example
   * ```typescript
   * const { data, pageCount, isLoading } = useQueryGetAll<UsersPaginated>(
   *   'users-paginated',
   *   { department: 'IT' },
   *   undefined,
   *   '/error',
   *   { prefetchNextPage: true, staleTime: 60000 }
   * );
   * ```
   */
  function useQueryGetAll<K extends Pagination, S = K>(
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    cacheTime?: number,
    navigateError?: string,
    options?: QueryGetAllOptions<K, S>
  ) {
    const { keyword } = useSearch();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const optionsDefault = {
      ...defaultOptionsQueryGetAll,
      ...options,
    };

    const params = useMemo(
      () =>
        cleanDeep({
          page,
          size: 25,
          search_query: keyword,
          ...payload,
        }),
      [keyword, payload]
    );

    const fetchData = async (prefetchNextPage = false) => {
      const paramPage = params.page || 1;
      const newParams = {
        ...params,
        page: prefetchNextPage ? paramPage + 1 : paramPage,
      };

      try {
        const res = await apiInstance(token).get<K>(url, {
          params: newParams,
          paramsSerializer: optionsDefault.paramsSerializer,
        });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          if (navigateError) {
            navigate(navigateError);
          }

          throw captureAdminDataAccessError({
            error,
            message: 'Terjadi Kesalahan useQueryGetAll',
            requestPath: url,
            httpMethod: 'GET',
            queryKey: 'useQueryGetAll error',
            operation: 'query_get_all',
            queryParams: newParams,
            extras: { [queryKey]: newParams },
            level: 'error',
          });
        }

        throw new Error('Terjadi Kesalahan useQueryGetAll');
      }
    };

    // Normalize notify props to the exact type expected by TanStack Query
    const ALLOWED_NOTIFY_KEYS: ReadonlyArray<
      keyof UseQueryResult<unknown, unknown>
    > = [
      'data',
      'dataUpdatedAt',
      'error',
      'errorUpdatedAt',
      'failureCount',
      'failureReason',
      'fetchStatus',
      'isError',
      'isFetched',
      'isFetchedAfterMount',
      'isFetching',
      'isInitialLoading',
      'isLoading',
      'isLoadingError',
      'isPaused',
      'isPending',
      'isPlaceholderData',
      'isRefetchError',
      'isRefetching',
      'isSuccess',
      'refetch',
      'status',
    ] as const;

    const normalizeNotifyProps = (
      v:
        | QueryGetAllOptions['notifyOnChangeProps']
        | QueryGetAllOptions['notifyOnChangePropsExclusions']
    ):
      | NotifyPropsTyped
      | Array<keyof UseQueryResult<unknown, unknown>>
      | undefined => {
      if (!v) return undefined;
      if (v === 'all' || typeof v === 'function') return v as NotifyPropsTyped;
      // v is string[] or typed array -> filter to allowed keys for runtime safety
      const arr = Array.isArray(v) ? v : [];
      const filtered = arr.filter(
        (k): k is keyof UseQueryResult<unknown, unknown> =>
          (ALLOWED_NOTIFY_KEYS as ReadonlyArray<string>).includes(k as string)
      );
      return filtered as Array<keyof UseQueryResult<unknown, unknown>>;
    };

    const normalizedNotifyProps = normalizeNotifyProps(
      optionsDefault.notifyOnChangeProps
    ) as NotifyPropsTyped | undefined;
    const normalizedNotifyPropsExclusions = normalizeNotifyProps(
      optionsDefault.notifyOnChangePropsExclusions
    ) as Array<keyof UseQueryResult<unknown, unknown>> | undefined;

    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
      isFetching,
      isFetched,
    } = useQuery<K, unknown, S>({
      queryKey: [queryKey, params.page, params],
      queryFn: () => fetchData(),
      placeholderData: keepPreviousData,
      gcTime: cacheTime,
      staleTime: optionsDefault.staleTime,
      enabled: optionsDefault.enabled,
      select: optionsDefault.select as unknown as (data: K) => S,
      // TanStack Query v5 removed notifyOnChangeProps options; selection + tracked props suffice.
    });

    useEffect(() => {
      if (optionsDefault.prefetchNextPage && optionsDefault.enabled) {
        queryClient.prefetchQuery({
          queryKey: [
            queryKey,
            (params.page || 1) + 1,
            { ...params, page: (params.page || 1) + 1 },
          ],
          queryFn: () => fetchData(true),
          staleTime: 30000,
          retry: false,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, params, options, queryKey]);

    return {
      data: data,
      pageCount:
        (data as unknown as Pagination | undefined)?.pagination?.pages ?? 0,
      total: (data as unknown as Pagination | undefined)?.pagination?.rows ?? 0,
      isLoading: isPending,
      isPreviousData,
      isFetching,
      isFetched,
      refetch,
    };
  }

  /**
   * GET request hook for fetching a specific resource by ID.
   * Only executes when a valid ID is provided.
   *
   * @template K - The expected response data type
   * @param id - The resource ID to fetch (query is disabled if null/undefined/empty)
   * @param queryKey - Unique identifier for the query cache
   * @param payload - Optional additional query parameters
   * @param retry - Optional flag to control query retry behavior
   *
   * @returns Object containing:
   *   - data: Response data for the specific resource
   *   - isLoading: Loading state boolean
   *   - isPreviousData: Whether showing previous data during transition
   *   - refetch: Function to manually refetch the data
   *
   * @example
   * ```typescript
   * const { data: user, isLoading } = useQueryGetById<User>(
   *   userId,
   *   'user-detail',
   *   { include_profile: true },
   *   false
   * );
   * ```
   */
  function useQueryGetById<K>(
    id: string | null,
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    retry?: boolean
  ) {
    const params = { ...payload };
    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(`${url}/${id}`, {
          params: cleanDeep(params),
        });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          throw captureAdminDataAccessError({
            error,
            message: 'Terjadi Kesalahan useQueryData',
            requestPath: `${url}/${id}`,
            httpMethod: 'GET',
            queryKey: 'useQueryGetAll error',
            operation: 'query_get_all',
            queryParams: params,
            extras: { [queryKey]: params },
            level: 'error',
          });
        }
        throw new Error('Terjadi Kesalahan useQueryGetById');
      }
    };
    const cleanedParams = cleanDeep(params);
    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
    } = useQuery<K>({
      queryKey: [queryKey, id, cleanedParams],
      queryFn: fetchData,
      enabled: id !== null && id !== undefined && id !== '',
      retry: retry,
    });

    return {
      data: data,
      isLoading: isPending,
      isPreviousData,
      refetch,
    };
  }

  /**
   * GET request hook with manual pagination state management and optional prefetching.
   * Manages page count and total records internally using useState.
   *
   * @template K - The expected data type for individual items
   * @param queryKey - Unique identifier for the query cache
   * @param payload - Optional additional query parameters
   * @param disablePrefetch - Optional flag to disable automatic next page prefetching
   *
   * @returns Object containing:
   *   - data: Array of response data items (defaults to empty array)
   *   - pageCount: Total number of pages (managed internally)
   *   - total: Total number of records (managed internally)
   *   - isLoading: Loading state boolean
   *   - isPreviousData: Whether showing previous data during transition
   *   - isStale: Whether the data is considered stale
   *   - isFetching: Whether currently fetching data
   *   - refetch: Function to manually refetch the data
   *
   * @example
   * ```typescript
   * const { data, pageCount, total, isLoading } = useQueryData<Product>(
   *   'products-data',
   *   { category: 'electronics' },
   *   false // Enable prefetching
   * );
   * ```
   */
  function useQueryData<K>(
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    disablePrefetch?: boolean
  ) {
    const [pageCount, setPageCount] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const { keyword } = useSearch();
    const queryClient = useQueryClient();
    const params = {
      page,
      size: 25,
      search_query: keyword,
      ...payload,
    };

    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get(url, {
          params: cleanDeep(params),
        });
        setPageCount(Number(res.data.pagination.pages));
        setTotal(Number(res.data.pagination.rows));
        return res.data.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          throw captureAdminDataAccessError({
            error,
            message: 'Terjadi Kesalahan useQueryData',
            requestPath: url,
            httpMethod: 'GET',
            queryKey: 'useQueryGetAll error',
            operation: 'query_get_all',
            queryParams: params,
            extras: { [queryKey]: params },
            level: 'error',
          });
        }
        throw new Error('Terjadi Kesalahan useQueryData');
      }
    };

    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
      isStale,
      isFetching,
    } = useQuery<K[]>({
      queryKey: [queryKey, page, keyword, payload],
      queryFn: fetchData,
      placeholderData: keepPreviousData,
    });

    useEffect(() => {
      if (disablePrefetch !== true) {
        queryClient.prefetchQuery({
          queryKey: [queryKey, page + 1, keyword],
          queryFn: fetchData,
          retry: false,
          staleTime: 30000,
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disablePrefetch, keyword, queryClient, queryKey]);

    return {
      data: data ?? [],
      pageCount,
      total,
      isLoading: isPending,
      isPreviousData,
      isStale,
      isFetching,
      refetch,
    };
  }

  /**
   * GET request hook for fetching a resource by ID with pagination support.
   * Combines resource-specific fetching with paginated results.
   *
   * @template K - The expected response type that extends Pagination interface
   * @param id - The resource ID to fetch
   * @param queryKey - Unique identifier for the query cache
   * @param payload - Optional additional query parameters
   * @param isRetry - Optional flag to control query retry behavior
   *
   * @returns Object containing:
   *   - data: Paginated response data for the specific resource
   *   - isLoading: Loading state boolean
   *   - isPreviousData: Whether showing previous data during transition
   *   - pageCount: Total number of pages from pagination
   *   - total: Total number of rows from pagination
   *   - refetch: Function to manually refetch the data
   *
   * @example
   * ```typescript
   * const { data, pageCount, total } = useQueryGetByIdPager<UserPostsPaginated>(
   *   userId,
   *   'user-posts',
   *   { status: 'published' },
   *   true
   * );
   * ```
   */
  function useQueryGetByIdPager<K extends Pagination>(
    id: string | null,
    queryKey: string,
    payload?: Readonly<QueryStringSearch>,
    isRetry?: boolean
  ) {
    const { keyword } = useSearch();

    const params = useMemo(
      () =>
        cleanDeep({
          page,
          size: 25,
          search_query: keyword,
          ...payload,
        }),
      [keyword, payload]
    );

    const fetchData = async () => {
      try {
        const res = await apiInstance(token).get<K>(`${url}/${id}`, { params });
        return res.data;
      } catch (error) {
        if (error instanceof AxiosError) {
          throw captureAdminDataAccessError({
            error,
            message: 'Terjadi Kesalahan useQueryData',
            requestPath: `${url}/${id}`,
            httpMethod: 'GET',
            queryKey: 'useQueryGetAll error',
            operation: 'query_get_all',
            queryParams: params,
            extras: { [queryKey]: params },
            level: 'error',
          });
        }
        throw new Error('Terjadi Kesalahan useQueryGetByIdPager');
      }
    };

    const {
      data,
      isPending,
      isPlaceholderData: isPreviousData,
      refetch,
    } = useQuery<K>({
      queryKey: [queryKey, id, params],
      queryFn: fetchData,
      retry: isRetry,
    });

    return {
      data: data,
      isLoading: isPending,
      isPreviousData,
      pageCount: data?.pagination?.pages || 0,
      total: data?.pagination?.rows || 0,
      refetch,
    };
  }

  return {
    useQueryGet,
    useQueryGetById,
    useQueryData,
    useQueryGetByIdPager,
    useQueryGetAll,
  };
}
